<script setup>
import {ref, computed, inject, onMounted} from "vue";
import HeightTransitionComposition from "./HeightTransitionComposition";
import useComment, {USE_COMMENT_KEY} from "./CommentComposition";
import CommentContainer from "./CommentContainer.vue";
import RuleModal from "./RuleModal.vue";

const siteUrl = document.querySelector('meta[name="site-url"]').content;

const COMMENT_ID = 'js-comment-'+Math.random().toString(36).slice(-8);

//コンポジションを定義、配信
const composition = inject(USE_COMMENT_KEY, useComment());

//コメント部分の開閉を扱うコンポジションだが、開いた状態で固定しておく
const heightTransition = HeightTransitionComposition();
heightTransition.open();

//CommentContainer を描画する準備フラグ
//CommentContainer は内部で teleport を使っているが、これは指定先の DOM がすでに描画済の場合のみ使用できる
//teleport 先は当コンポーネントが描画するので、このコンポーネントの onMounted を待つ必要があるため、タイミングをこの ref で調節する
const commentReady = ref(false);

//利用規約モーダルを表示するかどうか
const showRuleModal = ref(false);

/**
 * 投稿に対してコメント可能かどうかを判定する
 * @type {ComputedRef<unknown>}
 */
const isCanComment = computed(() => {
  //そもそもコメント不可設定だったら不可
  if (Number(composition.state.acceptComments) === 0) {
    return false;
  }

  //すでにコメントしたことがあるか判定
  return ! composition.state.comments.some(thread => {
    //一つ目のコメントが存在しなけば飛ばす
    if (
      ! thread[0] ||
      typeof thread[0] !== 'object' ||
      ! ('is_self' in thread[0])
    ) {
      return false;
    }

    //判定を返す
    return Number(thread[0].is_self) !== 0;
  });
});

//初期化処理
onMounted(() => {
  //コメント描画の準備を行う
  commentReady.value = true;

  // Twitter 向けのコード追加
  if ( ! document.getElementById('social-script-twitter')) {
    const script = document.createElement('script');
    /* <script async src="https://platform.twitter.com/widgets.js" charset="utf-8"> */
    script.async = true;
    script.src = 'https://platform.twitter.com/widgets.js';
    script.charset = 'utf-8';
    script.id = 'social-script-twitter';
    document.body.appendChild(script);
  }

  // LINE 向けのコード追加
  if ( ! document.getElementById('social-script-line')) {
    /*
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.textContent = 'LineIt.loadButton();';
    script.id = 'social-script-line';
    document.body.appendChild(script);
     */
  }
});
</script>

<template>
  <div
    v-show="! composition.state.isMyPost && isCanComment"
    class="gbMainInner"
  >
    <section id="commentPost">
      <h2 class="lyVerticalCnt lyMb16 lyPt8 lyPb8 lyPr4 lyPl4 pFont16 pFontBold pBorderBottom2pxSolidBlack">
        コメントを書く
        <span class="lyMl8 pFont10 pFontGray50 pFontMedium">利用規約を遵守の上、投稿お願いします</span>
      </h2>
      <div class="lyPt8 lyMb16">
        <ul class="lyMb8">
          <li class="lyVerticalStart pFont12 pFontMedium">
            <span class="lyBlock lyShrinkNone lyW16px lyMt2 lyMr2"><img
              :src="siteUrl+'images/iconViolationBlack.svg'"
              width="15"
              height="14"
              loading="lazy"
              alt=""
            ></span>
            他人への誹謗中傷や名誉毀損にあたる内容は禁止
          </li>
          <li class="lyVerticalStart pFont12 pFontMedium">
            <span class="lyBlock lyShrinkNone lyW16px lyMt2 lyMr2"><img
              :src="siteUrl+'images/iconViolationBlack.svg'"
              width="15"
              height="14"
              loading="lazy"
              alt=""
            ></span>
            他人に不快感を与えるコメントは禁止
          </li>
          <li class="lyVerticalStart pFont12 pFontMedium">
            <span class="lyBlock lyShrinkNone lyW16px lyMt2 lyMr2"><img
              :src="siteUrl+'images/iconViolationBlack.svg'"
              width="15"
              height="14"
              loading="lazy"
              alt=""
            ></span>
            個人情報の入力は禁止
          </li>
        </ul>
        <p class="lyPb8">
          <button
            class="lyVerticalCnt lyPt4 lyPr4 lyPb4 pFont12 pFontMedium"
            @click="showRuleModal = true"
          >
            詳しいルールを確認する
            <span class="lyBlock lyShrinkNone lyW16px lyMl2"><img
              :src="siteUrl+'images/iconDocument.svg'"
              width="15"
              height="14"
              loading="lazy"
              alt=""
            ></span>
          </button>
        </p>
      </div>
      <div :id="COMMENT_ID"></div>
      <CommentContainer
        v-if="commentReady"
        :comment-id="COMMENT_ID"
        :parent-id="0"
        :height-transition="heightTransition"
      />
    </section>
    <teleport to="body">
      <RuleModal
        :is-show="showRuleModal"
        @request-close="showRuleModal = false"
      />
    </teleport>
  </div>
</template>
